import DialogService from './dialogs/DialogService';
import { getValueFromSessionStorage, saveValueToSessionStorage } from './localStorage.service';
import loggerService from './logger.service';
import { getTrackingValues } from './mediaTracking.utils';

import analytics from '@/analytics';
import Dialog from '@/components/core/Dialog.vue';
import { AGORA, LOCAL_STORAGE_KEYS } from '@/constants';
import store from '@/store';
import { ProgressiveCloudProxyStatus, TurnServerFallbackStatus } from '@/store/modules/mediaroom/types';
import LiteMode from '@/views/event/LiteMode.vue';
import ConnectionFailedDialog from '@/views/event/TechCheck/ConnectionFailedDialog.vue';

const CLOUD_PROXY_MODES = [5, 3];
let dailogIndex: number | null = null;

/**
 * Proxy mode configured on the organization (`cloudproxy_mode`), or undefined when the org
 * connects directly. Only modes present in CLOUD_PROXY_MODES are honoured.
 */
export function getOrgCloudProxyMode(): number | undefined {
  const orgMode = Number(store.state.event.organization?.cloudproxy_mode || 0);
  return CLOUD_PROXY_MODES.includes(orgMode) ? orgMode : undefined;
}

/**
 * Order in which proxy modes are tried. The mode that last succeeded in this session goes first,
 * otherwise the org's configured mode, so whichever mode is reused first falls through to the
 * remaining modes instead of being retried.
 */
export function getCloudProxyModes(): number[] {
  const preferredMode = parseInt(getValueFromSessionStorage(LOCAL_STORAGE_KEYS.preferredCloudProxyMode));
  const firstMode = CLOUD_PROXY_MODES.includes(preferredMode) ? preferredMode : getOrgCloudProxyMode();
  if (firstMode === undefined) {
    return CLOUD_PROXY_MODES;
  }
  return [firstMode, ...CLOUD_PROXY_MODES.filter(mode => mode !== firstMode)];
}

export function getCloudProxyModeIndex(mode: number): number {
  return Math.max(getCloudProxyModes().indexOf(mode), 0);
}

export function shouldCreateTracksForChannel(channelName: string, userRole: string): boolean {
  // TODO: Remove this once we know why staff page have audince role
  if (channelName === AGORA.TEST_CHANNEL) {
    return true;
  }
  const isBackstageChannel = channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE);

  if (isBackstageChannel) {
    // For backstage channels: only create tracks if main stage user role is audience
    const mainStageChannelId = channelName.replace(AGORA.UID_SUFFIX.BACKSTAGE, '');
    const mainStageRoom = store.state.mediaroom.mediaRooms[mainStageChannelId];
    const isMainStageUserAudience = mainStageRoom?.userRole === AGORA.ROLE.ATTENDEE;

    return isMainStageUserAudience;
  }
  // For non-backstage channels: only create tracks if user role is host
  return userRole === AGORA.ROLE.SPEAKER;
}

export function verifyAndSaveProxyMode(channelName: string) {
  const channelState = store.state.mediaroom.channelPeerConnectionStates[channelName];

  if (
    channelState?.progressiveCloudProxyMode !== undefined &&
    channelState?.isProgressiveCloudProxyStatus === ProgressiveCloudProxyStatus.IN_PROGRESS
  ) {
    if (dailogIndex !== null) {
      DialogService.close(dailogIndex);
    }
    const successfulProxyMode = channelState.progressiveCloudProxyMode;
    loggerService.warn('[AGORA] Progressive cloud proxy successful', {
      channelName: channelName,
      proxyMode: successfulProxyMode
    });

    analytics.track('progressive-cloud-proxy-success', {
      ...getTrackingValues(store.state.mediaroom.mediaRooms[channelName]),
      channelName: channelName,
      reason: 'peer-connection-success',
      proxyMode: successfulProxyMode
    });

    // Persist preferred mode for future sessions
    saveValueToSessionStorage(LOCAL_STORAGE_KEYS.preferredCloudProxyMode, successfulProxyMode.toString());

    store.commit('mediaroom/updateChannelPeerConnectionState', {
      channelName,
      updates: {
        progressiveCloudProxyMode: successfulProxyMode,
        isProgressiveCloudProxyStatus: ProgressiveCloudProxyStatus.SUCCESS,
        currentProxyModeIndex: 0
      }
    });
  }
  if (channelState?.turnServerFallbackStatus === TurnServerFallbackStatus.IN_PROGRESS) {
    if (dailogIndex !== null) {
      DialogService.close(dailogIndex);
    }
    loggerService.warn('[AGORA] Turn server fallback successful', {
      channelName: channelName,
      turnServerUrl: channelState.turnServerUrl
    });
    saveValueToSessionStorage(LOCAL_STORAGE_KEYS.preferredTurnServerUrl, channelState.turnServerUrl);
    store.commit('mediaroom/updateChannelPeerConnectionState', {
      channelName,
      updates: {
        turnServerFallbackStatus: TurnServerFallbackStatus.SUCCESS,
        turnServerUrl: channelState.turnServerUrl
      }
    });
    analytics.track('turn_server_fallback_success', {
      ...getTrackingValues(store.state.mediaroom.mediaRooms[channelName]),
      channelName: channelName
    });
    analytics.track('progressive-cloud-proxy-success', {
      ...getTrackingValues(store.state.mediaroom.mediaRooms[channelName]),
      channelName: channelName,
      reason: 'peer-connection-success'
    });
  }
}

export async function handlePeerConnectionTimeout(channelName: string) {
  // An org-level proxy mode always needs the fallback chain, otherwise a failing org mode has no way out.
  if (!store.state.event.useCloudProxyFlow && getOrgCloudProxyMode() === undefined) {
    return;
  }

  const channelState = store.state.mediaroom.channelPeerConnectionStates[channelName];

  if (
    channelState.isProgressiveCloudProxyStatus === ProgressiveCloudProxyStatus.EXHAUSTED &&
    channelState.turnServerFallbackStatus === TurnServerFallbackStatus.FAILED
  ) {
    return;
  }
  analytics.track('agora_peer_connection_timeout', {
    ...getTrackingValues(store.state.mediaroom.mediaRooms[channelName]),
    connectionStatus: store.state.mediaroom.channelPeerConnectionStates[channelName].status,
    currentPeerConnectionState: store.state.mediaroom.channelPeerConnectionStates[channelName].peerConnectionState,
    turnServerUrl: store.state.mediaroom.channelPeerConnectionStates[channelName].turnServerUrl,
    turnServerFallbackStatus: store.state.mediaroom.channelPeerConnectionStates[channelName].turnServerFallbackStatus
  });

  loggerService.warn('[AGORA] Peer connection timeout detected, handling progressive cloud proxy', {
    channelName: channelName
  });

  if (channelState?.isProgressiveCloudProxyStatus === ProgressiveCloudProxyStatus.IN_PROGRESS) {
    loggerService.warn('[AGORA] Progressive cloud proxy attempt failed', {
      channelName: channelName,
      proxyMode: channelState.progressiveCloudProxyMode
    });

    analytics.track('progressive-cloud-proxy-failed', {
      ...getTrackingValues(store.state.mediaroom.mediaRooms[channelName]),
      channelName: channelName,
      proxyMode: channelState.progressiveCloudProxyMode
    });

    const currentIndex = (channelState.currentProxyModeIndex ?? 0) + 1;
    if (currentIndex >= getCloudProxyModes().length) {
      await handleProxyExhausted(channelName, 'agora_peer_connection_timeout');
      return;
    }

    store.commit('mediaroom/updateChannelPeerConnectionState', {
      channelName,
      updates: {
        currentProxyModeIndex: currentIndex
      }
    });

    await tryNextCloudProxyMode(channelName, 'agora_peer_connection_timeout');
  } else if (channelState?.turnServerFallbackStatus === TurnServerFallbackStatus.IN_PROGRESS) {
    loggerService.error('[AGORA] All cloud proxy modes already exhausted and TURN server fallback failed', {
      channelName
    });

    store.commit('mediaroom/updateChannelPeerConnectionState', {
      channelName,
      updates: {
        turnServerFallbackStatus: TurnServerFallbackStatus.FAILED,
        turnServerUrl: undefined
      }
    });
    analytics.track('turn_server_fallback_failed', {
      ...getTrackingValues(store.state.mediaroom.mediaRooms[channelName]),
      channelName: channelName
    });
    const router = store.state.route.fullPath;
    if (router.includes('/broadcast') || router.includes('/discussion-room')) {
      const isLiteModeSwitchDialog = shouldShowLiteModeSwitchDialog(channelName);
      if (isLiteModeSwitchDialog) {
        if (dailogIndex !== null) {
          DialogService.close(dailogIndex);
        }
        dailogIndex = DialogService.show(
          LiteMode,
          {
            size: 'small',
            showOverlay: false
          },
          {
            on: {
              onSwitchToLiteMode: () => {
                store.dispatch('requestLiteModeSwitch');
              },
              onCancel: () => {
                dailogIndex = null;
              }
            }
          }
        ).index;
      } else {
        if (dailogIndex !== null) {
          DialogService.close(dailogIndex);
        }
        dailogIndex = DialogService.show(ConnectionFailedDialog, {
          size: 'small',
          isTechCheck: false
        }).index;
      }
      return;
    }
  } else {
    store.commit('mediaroom/updateChannelPeerConnectionState', {
      channelName,
      updates: {
        currentProxyModeIndex: 0
      }
    });
    await tryNextCloudProxyMode(channelName, 'agora_peer_connection_timeout');
  }
}

export async function tryNextCloudProxyMode(
  channelName: string,
  reason: string,
  isAttemptingTurnServerFallback: boolean = false
) {
  const channelState = store.state.mediaroom.channelPeerConnectionStates[channelName];
  const currentIndex = channelState?.currentProxyModeIndex || 0;
  let proxyModeIndex = currentIndex;
  let proxyMode = getCloudProxyModes()[proxyModeIndex];

  if (channelState?.isProgressiveCloudProxyStatus === ProgressiveCloudProxyStatus.SUCCESS) {
    loggerService.warn('[AGORA] Using preferred cloud proxy mode', {
      channelName: channelName,
      proxyMode: parseInt(getValueFromSessionStorage(LOCAL_STORAGE_KEYS.preferredCloudProxyMode))
    });
    proxyMode = parseInt(getValueFromSessionStorage(LOCAL_STORAGE_KEYS.preferredCloudProxyMode));
    // Point the index at the reused mode so the next timeout advances to a mode not yet tried.
    proxyModeIndex = getCloudProxyModeIndex(proxyMode);
  }
  if (isAttemptingTurnServerFallback) {
    loggerService.warn('[AGORA] Trying turn server fallback', {
      channelName,
      reason
    });
  } else {
    loggerService.warn('[AGORA] Trying cloud proxy mode', {
      channelName,
      proxyMode,
      reason
    });
  }

  try {
    if (isAttemptingTurnServerFallback) {
      store.commit('mediaroom/updateChannelPeerConnectionState', {
        channelName,
        updates: {
          progressiveCloudProxyMode: 'turn_server_fallback',
          turnServerUrl: channelState.turnServerUrl,
          turnServerFallbackStatus: TurnServerFallbackStatus.IN_PROGRESS
        }
      });
    } else {
      store.commit('mediaroom/updateChannelPeerConnectionState', {
        channelName,
        updates: {
          progressiveCloudProxyMode: proxyMode,
          isProgressiveCloudProxyStatus: ProgressiveCloudProxyStatus.IN_PROGRESS,
          currentProxyModeIndex: proxyModeIndex
        }
      });
    }

    analytics.track('progressive-cloud-proxy-attempt', {
      ...getTrackingValues(store.state.mediaroom.mediaRooms[channelName]),
      channelName,
      reason,
      proxyMode
    });

    const mediaRoom = store.state.mediaroom.mediaRooms[channelName];
    const userRole = mediaRoom.userRole;
    const mode = mediaRoom.mode;

    await store.dispatch('mediaroom/exitRoom', { channelName });

    await store
      .dispatch('mediaroom/joinRoom', {
        channelName,
        role: userRole,
        mode,
        forceFetchFromApi: false
      })
      .then(() => {
        if (userRole) {
          // Only create tracks if conditions are met
          const shouldCreateTracks = shouldCreateTracksForChannel(channelName, userRole);

          if (shouldCreateTracks) {
            store.dispatch('mediaroom/createLocalAudioVideoTracks', {
              channelName
            });
          }
        }
      });
  } catch (error) {
    loggerService.error('[AGORA] Progressive cloud proxy attempt error', {
      channelName,
      proxyMode,
      error
    });

    const nextIndex = proxyModeIndex + 1;
    store.commit('mediaroom/updateChannelPeerConnectionState', {
      channelName,
      updates: {
        currentProxyModeIndex: nextIndex
      }
    });
    await tryNextCloudProxyMode(channelName, reason);
  }
}

async function handleProxyExhausted(channelName: string, reason: string) {
  loggerService.warn('[AGORA] All cloud proxy modes already exhausted', {
    channelName,
    reason
  });

  analytics.track('progressive-cloud-proxy-exhausted', {
    ...getTrackingValues(store.state.mediaroom.mediaRooms[channelName]),
    channelName,
    reason
  });

  store.commit('mediaroom/updateChannelPeerConnectionState', {
    channelName,
    updates: {
      isProgressiveCloudProxyStatus: ProgressiveCloudProxyStatus.EXHAUSTED
    }
  });
  if (
    store.state.mediaroom.channelPeerConnectionStates[channelName]?.turnServerFallbackStatus ===
    TurnServerFallbackStatus.NOT_STARTED
  ) {
    await tryNextCloudProxyMode(channelName, reason, true);
  }
  // Show connection failed dialog if TURN server fallback also fails
  if (
    store.state.mediaroom.channelPeerConnectionStates[channelName]?.turnServerFallbackStatus ===
      TurnServerFallbackStatus.FAILED &&
    store.state.mediaroom.channelPeerConnectionStates[channelName]?.isProgressiveCloudProxyStatus ===
      ProgressiveCloudProxyStatus.EXHAUSTED
  ) {
    const router = store.state.route.fullPath;
    if (router.includes('/broadcast')) {
      if (dailogIndex !== null) {
        DialogService.close(dailogIndex);
      }
      dailogIndex = DialogService.show(ConnectionFailedDialog, {
        size: 'small',
        isTechCheck: false
      }).index;
    }
  }
}

function shouldShowLiteModeSwitchDialog(channelName: string): boolean {
  const currentBroadcast = store.getters['event/currentBroadcast'];
  if (!currentBroadcast?.is_recording) {
    return false;
  }

  const mainStageChannelId = channelName.replace(AGORA.UID_SUFFIX.BACKSTAGE, '');
  const backstageChannelId = mainStageChannelId + AGORA.UID_SUFFIX.BACKSTAGE;

  const { mediaRooms } = store.state.mediaroom;
  const mainStageRoom = mediaRooms[mainStageChannelId] ?? {};
  const backstageRoom = mediaRooms[backstageChannelId] ?? {};

  const isMainStageUserAudience = mainStageRoom?.userRole === AGORA.ROLE.ATTENDEE;

  const isBackstageUserAudience =
    backstageRoom?.userRole === AGORA.ROLE.ATTENDEE || backstageRoom?.userRole === undefined;

  return isMainStageUserAudience && isBackstageUserAudience;
}
